import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, MessageCircle, X, Sparkles, Shield, Building2, Users } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../../components/ui/Button'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { ChatInterface } from '../../components/chat/ChatInterface'
import { Logo } from '../../components/ui/Logo'
import { useIsMobile } from '../../hooks/useIsMobile'
import { PublicFooter } from '../../components/layout/PublicFooter'
import { PublicNavigation } from '../../components/layout/PublicNavigation'
import toast from 'react-hot-toast'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [showSSOModal, setShowSSOModal] = useState(false)
  const [ssoProvider, setSSOProvider] = useState<'apple' | 'google' | null>(null)
  const { login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  
  const from = location.state?.from?.pathname || '/app'

  const handleStartProcess = () => {
    // Account creation happens through chat
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password)
      toast.success('Welcome back!')
      navigate(from, { replace: true })
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.')
    }
  }

  const handleAppleSignIn = () => {
    setSSOProvider('apple')
    setShowSSOModal(true)
    // Simulate SSO flow
    setTimeout(() => {
      setShowSSOModal(false)
      toast('Apple Sign-In integration coming soon!', {
        icon: '🍎',
        duration: 3000,
      })
    }, 2000)
  }

  const handleGoogleSignIn = () => {
    setSSOProvider('google')
    setShowSSOModal(true)
    // Simulate SSO flow
    setTimeout(() => {
      setShowSSOModal(false)
      toast('Google Sign-In integration coming soon!', {
        icon: '🔐',
        duration: 3000,
      })
    }, 2000)
  }

  // Mobile: Full screen mode
  if (showChat && isMobile) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button 
                variant="outline" 
                onClick={() => setShowChat(false)}
                className="mb-4"
              >
                ← Back to Login
              </Button>
              <h1 className="text-3xl font-bold text-center mb-2">1031 Exchange Assistant</h1>
              <p className="text-center text-muted-foreground">
                Get personalized guidance from Neela
              </p>
            </div>
            <div className="h-[600px]">
              <ChatInterface 
                onStartProcess={handleStartProcess}
                className="h-full"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden flex flex-col transition-colors duration-300">

      <PublicNavigation />

      {/* Main */}
      <div className="flex flex-col lg:flex-row flex-1 relative z-10 gap-8 lg:gap-0">
        {/* Left side - Form */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="mx-auto w-full max-w-sm lg:w-96 bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] p-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Sign in to your account
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Or{' '}
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openChat'))
                }}
                className="font-medium text-[#69adba] hover:text-[#5a9aa6] underline"
              >
                create a new account
              </button>
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    className="input w-full bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:border-[#69adba] dark:focus:border-[#69adba]"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="input w-full pr-10 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:border-[#69adba] dark:focus:border-[#69adba]"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#69adba] focus:ring-[#69adba] border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-900 dark:text-slate-100">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-[#69adba] hover:text-[#5a9aa6]">
                  Forgot your password?
                </a>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className="w-full flex justify-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                ) : null}
                Sign in
              </Button>
            </div>
          </form>

          {/* SSO Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300 dark:border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white/60 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">Or continue with</span>
              </div>
            </div>

            {/* SSO Buttons */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              {/* Apple Sign-In */}
              <button
                type="button"
                onClick={handleAppleSignIn}
                className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm bg-black hover:bg-slate-900 text-white font-medium transition-colors"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Apple
              </button>

              {/* Google Sign-In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium transition-colors"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>
            </div>
          </div>
        </div>
        </div>

        {/* Right side - Info */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="mx-auto w-full max-w-sm lg:w-96 bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] p-8">
            <div className="text-center text-slate-900 dark:text-slate-100">
              <h3 className="text-3xl font-bold mb-4">
                Welcome to Open1031
              </h3>
              <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">
                The complete platform for 1031 exchanges
              </p>
              <div className="space-y-4 text-left">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4 text-[#69adba]" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">AI-powered document processing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 text-[#69adba]" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">Automated compliance checks</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-4 w-4 text-[#69adba]" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">Curated property marketplace</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                    <Users className="h-4 w-4 text-[#69adba]" />
                  </div>
                  <span className="text-slate-700 dark:text-slate-300">Expert guidance throughout</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />

      {/* Desktop: Docked Chat Panel (bottom-right) */}
      {showChat && !isMobile && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[420px] max-w-[90vw] h-[600px] min-h-[420px] max-h-[85vh] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden resize-y">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-800">
            <div className="flex items-center space-x-2">
              <Logo className="h-8 w-auto" />
            </div>
            <button
              onClick={() => setShowChat(false)}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
          <div className="h-[calc(100%-56px)]">
            <ChatInterface 
              onStartProcess={handleStartProcess}
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Floating Chat Button (when chat is not open) */}
      {!showChat && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          {/* Pulsing Ring Animation */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#69adba] to-[#5a9aa6] animate-ping opacity-20"></div>
          
          {/* Main Button */}
          <button
            onClick={() => setShowChat(true)}
            className="relative group h-16 w-16 lg:h-18 lg:w-18 bg-gradient-to-br from-[#69adba] via-[#5fbdce] to-[#69adba] hover:from-[#5fbdce] hover:via-[#69adba] hover:to-[#5fbdce] text-white rounded-full shadow-[0_20px_60px_rgba(105,173,186,0.5),0_8px_24px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.1)_inset] hover:shadow-[0_24px_72px_rgba(105,173,186,0.6),0_12px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.2)_inset] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-500 ease-out"
          >
            {/* Glossy Shine Effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
            
            {/* Icon with subtle animation */}
            <MessageCircle className="h-8 w-8 lg:h-9 lg:w-9 relative z-10 drop-shadow-lg group-hover:rotate-12 transition-transform duration-300" />
          </button>
        </div>
      )}

      {/* SSO Modal - Mockup */}
      {showSSOModal && (
        <div className="modal-overlay">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in zoom-in duration-200 border border-slate-200 dark:border-slate-700">
            <div className="text-center">
              {/* Provider Logo */}
              <div className="mb-6 flex justify-center">
                {ssoProvider === 'apple' ? (
                  <div className="w-20 h-20 bg-black rounded-2xl flex items-center justify-center">
                    <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 rounded-2xl flex items-center justify-center">
                    <svg className="w-12 h-12" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                )}
              </div>

              {/* Message */}
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {ssoProvider === 'apple' ? 'Connecting to Apple' : 'Connecting to Google'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Securely authenticating with {ssoProvider === 'apple' ? 'Apple ID' : 'Google Account'}...
              </p>

              {/* Loading Spinner */}
              <div className="flex justify-center">
                <LoadingSpinner size="lg" />
              </div>

              {/* Info Text */}
              <p className="mt-6 text-xs text-slate-500 dark:text-slate-400">
                This is a demo of the SSO flow. Integration coming soon!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
